import { sectionLabels } from "../section-labels";
/**
 * Footer section — locked. Always rendered at the bottom. Contains store info
 * + policy links + tracking/help. Settings are minimal because most footer
 * content is data-driven (store identity, enabled policies).
 */
export const footerSchema = {
    type: "footer",
    name: sectionLabels["footer"],
    description: {
        ar: "القسم السفلي بمعلومات المتجر وروابط السياسات.",
        en: "The bottom block with store info and policy links.",
    },
    locked: true,
    criticalForSeo: true,
    maxPerTemplate: 1,
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "تصميم التذييل", en: "Footer style" },
            helpText: { ar: "شكل تنظيم التذييل.", en: "How the footer is laid out." },
            options: [
                { value: "footer-columns", label: { ar: "أعمدة", en: "Columns" } },
                { value: "footer-stacked", label: { ar: "موسّط", en: "Centered" } },
                { value: "footer-minimal", label: { ar: "مبسّط", en: "Minimal" } },
                { value: "footer-bar", label: { ar: "شريط", en: "Bar" } },
                { value: "footer-split", label: { ar: "مقسّم", en: "Split" } },
                { value: "footer-directory", label: { ar: "دليل", en: "Directory" } },
                { value: "footer-boutique", label: { ar: "بوتيك", en: "Boutique" } },
                { value: "footer-brand", label: { ar: "علامة", en: "Brand" } },
                { value: "footer-accordion", label: { ar: "قوائم مطوية", en: "Accordion" } },
                { value: "footer-wave", label: { ar: "نشرة بارزة", en: "Newsletter-led" } },
                { value: "footer-contact", label: { ar: "تواصل", en: "Contact-led" } },
                { value: "footer-social", label: { ar: "تواصل اجتماعي", en: "Social-led" } },
            ],
            default: "footer-columns",
        },
        {
            id: "showPolicyLinks",
            type: "checkbox",
            label: { ar: "إظهار روابط السياسات", en: "Show policy links" },
            helpText: {
                ar: "روابط الشحن والاسترجاع والاستبدال للسياسات المفعّلة.",
                en: "Shipping, refund, and exchange policy links for enabled policies.",
            },
            default: true,
        },
        {
            id: "showTrackOrderLink",
            type: "checkbox",
            label: { ar: "إظهار رابط تتبع الطلب", en: 'Show "Track order" link' },
            default: true,
        },
        // REMOVED: `showWhatsappLink`. It was a checkbox over a number that lives
        // in store settings, not here — tick it with no number saved and the footer
        // renders nothing, with no empty state and no route to the thing that would
        // fix it. The number's PRESENCE is the visibility now: `buildWhatsAppHref`
        // already returns null without one, which is the same mechanism the
        // platform uses to withhold the number from a restricted store.
        {
            id: "tagline",
            type: "text",
            i18n: true,
            label: { ar: "شعار التذييل", en: "Footer tagline" },
            helpText: {
                ar: "سطر قصير اختياري بأسفل التذييل.",
                en: "Optional short line at the bottom of the footer.",
            },
            validate: { max: 80 },
            // A10: the compact `footer-bar` variant has no tagline slot.
            showIf: {
                variant: [
                    "footer-columns",
                    "footer-stacked",
                    "footer-minimal",
                    "footer-split",
                    "footer-directory",
                    "footer-boutique",
                    "footer-brand",
                    "footer-accordion",
                    "footer-wave",
                    "footer-contact",
                    "footer-social",
                ],
            },
        },
        // ── Directory parts ────────────────────────────────────────────
        // Every EasyOrders footer runs SHOP / PAGES / CONTACT US columns closed by
        // a split bottom bar; ours ran SHOP / HELP (with the policies folded into
        // HELP) and a single centred copyright line. These three switches turn the
        // missing parts on. All default OFF, so a store that does not opt in keeps
        // exactly the footer it had.
        {
            id: "showPagesColumn",
            type: "checkbox",
            label: { ar: "عمود الصفحات", en: "Pages column" },
            helpText: {
                ar: "يفصل روابط السياسات في عمود مستقل بدل ما تكون تحت «المساعدة».",
                en: "Splits the policy links into their own column instead of nesting them under Help.",
            },
            default: false,
        },
        {
            id: "showContactColumn",
            type: "checkbox",
            label: { ar: "عمود بيانات التواصل", en: "Contact column" },
            helpText: {
                ar: "عمود بالتليفون والإيميل والعنوان.",
                en: "A column with your phone, email and address.",
            },
            default: false,
        },
        ...[
            ["contactPhone", "Phone", "التليفون"],
            ["contactEmail", "Email", "الإيميل"],
            ["contactAddress", "Address", "العنوان"],
        ].map(([id, en, ar]) => ({
            id,
            type: "text",
            label: { ar, en },
            validate: { max: 120 },
            showIf: { showContactColumn: true },
        })),
        {
            id: "splitBottomBar",
            type: "checkbox",
            label: { ar: "شريط سفلي مقسوم", en: "Split bottom bar" },
            helpText: {
                ar: "حقوق النشر على جنب و«مدعوم من» على الجنب التاني.",
                en: 'Copyright on one side, "powered by" on the other.',
            },
            default: false,
        },
        // ── Social links ───────────────────────────────────────────────
        // Every EasyOrders footer carries a social row (7–8 networks). We had
        // nothing to render from: there are no social fields on `Store`, and the
        // footer only ever received `whatsappNumber`. These live on the section
        // rather than the store record so they need no migration — the trade-off
        // is that they are per-theme, not global. Move them to `StoreSettings`
        // if they ever need to appear outside the footer.
        {
            id: "showSocials",
            type: "checkbox",
            label: { ar: "إظهار حسابات التواصل", en: "Show social links" },
            helpText: {
                ar: "صف أيقونات للحسابات اللي تكتب لينكها تحت.",
                en: "A row of icons for whichever accounts you fill in below.",
            },
            default: true,
        },
        ...[
            ["Facebook", "فيسبوك", "facebook"],
            ["Instagram", "إنستجرام", "instagram"],
            ["TikTok", "تيك توك", "tiktok"],
            ["X (Twitter)", "إكس (تويتر)", "x"],
            ["YouTube", "يوتيوب", "youtube"],
            ["Snapchat", "سناب شات", "snapchat"],
            ["LinkedIn", "لينكد إن", "linkedin"],
        ].map(([en, ar, key]) => ({
            id: `social${key.charAt(0).toUpperCase()}${key.slice(1)}`,
            type: "url",
            label: { ar, en },
            showIf: { showSocials: true },
        })),
        // ── Payment badges ─────────────────────────────────────────────
        // Their footers show card-brand art. We deliberately do NOT reproduce the
        // Visa/Mastercard/Meeza marks: `StorePaymentOption` models the seller's own
        // methods (WALLET/INSTAPAY/BANK/OTHER), not card networks, so claiming a
        // network the store may not accept would be a false trust signal — and
        // hand-drawn approximations of trademarked logos are worse than none.
        // These render as labelled badges; a seller who wants exact brand art can
        // upload it as a banner image instead.
        {
            id: "showPaymentBadges",
            type: "checkbox",
            label: { ar: "إظهار طرق الدفع", en: "Show payment methods" },
            helpText: {
                ar: "شارات نصية للطرق اللي بتقبلها.",
                en: "Text badges for the methods you accept.",
            },
            default: true,
        },
        ...[
            ["Cash on delivery", "الدفع عند الاستلام", "Cod"],
            ["Vodafone Cash", "فودافون كاش", "Wallet"],
            ["InstaPay", "إنستا باي", "Instapay"],
            ["Bank transfer", "تحويل بنكي", "Bank"],
            ["Card", "بطاقة", "Card"],
        ].map(([en, ar, key]) => ({
            id: `pay${key}`,
            type: "checkbox",
            label: { ar, en },
            default: key === "Cod",
            showIf: { showPaymentBadges: true },
        })),
    ],
};
//# sourceMappingURL=footer.js.map